import { useProjectStore, useUserStore } from '@talon/web/src/context';
import React, { useEffect, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { ChatMessage, MessageType } from '../../../../lib/api';
import { env } from '../../../config/env';
import { matchInboxEcho } from './matchInboxEcho';

export type InboxMessage = Partial<ChatMessage> & {
  client_id?: string;
  localId?: string;
  status?: 'sending' | 'sent' | 'failed' | 'unconfirmed';
};

export type ITypingPayload = {
  conversation_id: string;
  user_id: string;
  first_name: string;
};

interface ISupportChat {
  setMessageState: React.Dispatch<
    React.SetStateAction<{
      conversationId?: string;
      messages: InboxMessage[];
      pending?: string;
    }>
  >;
  onTyping?: (payload: ITypingPayload) => void;
  messageToSend?: InboxMessage;
  conversationId: string;
  onConnectedChange?: (connected: boolean) => void;
  onFailed?: (text: string) => void;
  onSent?: (text: string) => void;
  onReply?: () => void;
}

export const SupportChat = ({
  setMessageState,
  messageToSend,
  conversationId,
  onTyping,
  onReply,
  onConnectedChange,
  onFailed,
  onSent,
}: ISupportChat) => {
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  const queryParams = {
    c: conversationId,
  };
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(`${env.apiWsHost}:${env.apiPort}`, {
    queryParams,
    protocols: ['Authorization', userStore.token],
    onError: (e) => {
      console.log('error', e);
    },
    share: true,
  });
  const pendingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => {
    onConnectedChange?.(readyState === ReadyState.OPEN);
  }, [readyState, onConnectedChange]);
  useEffect(() => () => Object.values(pendingTimers.current).forEach(clearTimeout), []);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = lastMessage.data;
      const msgJson = JSON.parse(data) as InboxMessage;

      if (msgJson.type === MessageType.NOTIFICATION) return;

      switch (msgJson.type) {
        case MessageType.NEW_CONVERSATION:
        case MessageType.UPDATED_CONVERSATION:
          break;
        case MessageType.TYPING:
          onTyping && onTyping(msgJson as unknown as ITypingPayload);
          break;
        default:
          setMessageState((state) => {
            if (msgJson.id && state.messages.some((message) => message.id === msgJson.id)) return state;
            const matching = matchInboxEcho(state.messages, msgJson);
            if (matching?.localId) {
              clearTimeout(pendingTimers.current[matching.localId]);
              delete pendingTimers.current[matching.localId];
              if (typeof msgJson.text === 'string') queueMicrotask(() => onSent?.(msgJson.text || ''));
            }
            return {
              ...state,
              messages: matching
                ? state.messages.map((message) =>
                  message.localId === matching.localId ? { ...msgJson, status: 'sent' } : message
                )
                : [...state.messages, msgJson],
              conversationId: msgJson.conversation_id,
            };
          });
          // The bot always replies with AI_MESSAGE — on success with the LLM
          // output, and on error with the API's fallback message — so this is
          // the reliable signal that the turn is done and the waiting indicator
          // can be cleared.
          if (msgJson.type === MessageType.AI_MESSAGE) {
            // Bot tool calls change projects outside the browser's stores.
            void projectStore.fetchProjectList();
            onReply?.();
          }
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    // Ephemeral control frames (typing indicator, escape-cancel) are never persisted and never
    // echoed by the server, so they bypass the optimistic localId/client_id protocol below and
    // go straight to the wire — the pre-gate behavior these frames were built against.
    if (messageToSend?.type === MessageType.TYPING || messageToSend?.type === MessageType.CANCEL) {
      sendJsonMessage(messageToSend);
      return;
    }
    if (messageToSend?.localId) {
      const { localId, status: _status, created_at: _createdAt, ...payload } = messageToSend;
      if (readyState !== ReadyState.OPEN) {
        setMessageState((state) => ({
          ...state,
          messages: state.messages.map((message) =>
            message.localId === localId ? { ...message, status: 'failed' } : message
          ),
        }));
        onFailed?.(messageToSend.text || '');
        return;
      }
      sendJsonMessage(payload);
      clearTimeout(pendingTimers.current[localId]);
      pendingTimers.current[localId] = setTimeout(() => {
        setMessageState((state) => ({
          ...state,
          messages: state.messages.map((message) =>
            message.localId === localId && message.status === 'sending'
              ? { ...message, status: 'unconfirmed' }
              : message
          ),
        }));
      }, 5000);
    }
  }, [messageToSend]);
  return null;
};
